import { Box, Card, Flex, Stack, useMantineTheme } from '@mantine/core';
import type { DataAccessRequestTimeSeries } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { SelectorTarget, SelectType } from '@repo/common/components';
import { Selector } from '@repo/common/components/Selectors/Selector';
import { Text, Title } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../../api';
import type { DateRange } from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import DateRangeButtonGroup from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import { dateFilterOptions } from '../../constants';
import type {
	RequestsChartDataPoint,
	TimeRange,
	TimeRangeValue,
	ViewOption,
} from '../../types';
import { MetricsOverviewStackedLineChart } from './MetricsOverviewStackedLineChart';
import { processTimeseriesData } from './utils';

export const RequestsOverviewChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
	}: {
		timeseries: DataAccessRequestTimeSeries;
		dateFilter: TimeRange;
		setDateFilter: (dateFilter: TimeRange) => void;
		setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
		timeRangeValue: TimeRangeValue;
		dateRangeOption: ViewOption;
		setDateRangeOption: (dateRangeOption: ViewOption) => void;
	}) => {
		const { user, workspace } = useAuthUser();
		const theme = useMantineTheme();

		const chartData = useMemo(() => {
			if (!timeseries?.data) return [];

			const sortedData = processTimeseriesData(timeseries.data);

			const mappedData: RequestsChartDataPoint[] = sortedData.map((point) => ({
				date: point.timestamp,
				Open: point.status_counts.open,
				Active: point.status_counts.granted,
				Denied: point.status_counts.rejected,
				Closed: point.status_counts.expired + point.status_counts.cancelled,
			}));

			return mappedData;
		}, [timeseries]);

		const channels = useMemo(
			() => [
				{
					label: 'Open',
					x: 'date' as keyof RequestsChartDataPoint,
					y: 'Open' as keyof RequestsChartDataPoint,
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/warning/default'),
					zIndex: 10,
				},
				{
					label: 'Active',
					x: 'date' as keyof RequestsChartDataPoint,
					y: 'Active' as keyof RequestsChartDataPoint,
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/success/default'),
					zIndex: 20,
				},
				{
					label: 'Denied',
					x: 'date' as keyof RequestsChartDataPoint,
					y: 'Denied' as keyof RequestsChartDataPoint,
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/caution/default'),
					zIndex: 30,
				},
				{
					label: 'Closed',
					x: 'date' as keyof RequestsChartDataPoint,
					y: 'Closed' as keyof RequestsChartDataPoint,
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/primary/default'),
					zIndex: 40,
				},
			],
			[]
		);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = chartData[dataIndex];
				if (!point) return null;

				const isRange =
					dateRangeOption === 'week' || dateRangeOption === 'month';
				const nextPoint = chartData[dataIndex + 1];
				const showRange = isRange && nextPoint;

				return (
					<Box p="sm">
						<Text mb="sm" size="sm" color="text/secondary/default">
							{showRange ? (
								<>
									{point.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
									})}
									{' - '}
									{nextPoint.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</>
							) : (
								point.date.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})
							)}
						</Text>
						<Flex direction="column" gap="xs" mt="sm">
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[0].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Open request </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point['Open']}
								</Text>
							</Flex>
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[1].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Access granted</Text>
								</Flex>
								<Text fw={500} size="sm">
									{point['Active']}
								</Text>
							</Flex>
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[2].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Access expiring soon</Text>
								</Flex>
								<Text fw={500} size="sm">
									{point['Denied']}
								</Text>
							</Flex>
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[3].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Access removed </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point['Closed']}
								</Text>
							</Flex>
						</Flex>
					</Box>
				);
			},
			[channels, chartData, dateRangeOption, theme.radius.md]
		);

		const handleDateFilterChange = useCallback(
			(values: string[]) => {
				const newDateFilter = values[0] as TimeRange;
				setDateFilter(newDateFilter);
				setTimeRangeValue({
					...timeRangeValue,
					timeRange: newDateFilter,
				});
				trackEvent(
					'requests/overview/date_filter_change',
					{
						date_filter: newDateFilter,
					},
					user,
					workspace!
				);
			},
			[setDateFilter, setTimeRangeValue, timeRangeValue, user, workspace]
		);

		const handleDateRangeChange = useCallback(
			(range: DateRange, optionId: string) => {
				setTimeRangeValue({
					timeRange: dateFilter,
					dateRange: range,
				});
				setDateRangeOption(optionId as ViewOption);
				trackEvent(
					'requests/overview/date_range_change',
					{
						date_range_option: optionId,
						date_range_from: range.from?.toISOString() ?? '',
						date_range_to: range.to?.toISOString() ?? '',
					},
					user,
					workspace!
				);
			},
			[dateFilter, setTimeRangeValue, setDateRangeOption, user, workspace]
		);

		const getDateFilterItems = useCallback(async () => dateFilterOptions, []);

		const selectedDateFilterLabel = useMemo(
			() =>
				dateFilterOptions.find((item) => item.value === dateFilter)?.label ??
				'Last 24 hours',
			[dateFilter]
		);

		return (
			<Card withBorder style={{ borderRadius: theme.radius.md }}>
				<Stack spacing="md">
					<Flex justify="space-between" align="center">
						<Title size="sm" weight="bold">
							Requests over time
						</Title>
						<Flex gap="sm">
							<Selector
								initialValue={[dateFilter]}
								getItems={getDateFilterItems}
								onChange={handleDateFilterChange}
								selectType={SelectType.Single}
								disableSearch
							>
								<SelectorTarget size="md" variant="default">
									{selectedDateFilterLabel}
								</SelectorTarget>
							</Selector>
							<DateRangeButtonGroup
								selectedOptionId={dateRangeOption}
								onChange={handleDateRangeChange}
							/>
						</Flex>
					</Flex>
					<MetricsOverviewStackedLineChart
						channels={channels}
						chartData={chartData}
						dateFilter={dateFilter}
						tooltipDataRenderer={tooltipDataRenderer}
						dateRangeOption={dateRangeOption}
					/>
				</Stack>
			</Card>
		);
	}
);
